import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MessageCircle, X, Send } from "lucide-react";

/* ═══════════════════════════════════════════════════════
   BusiGrow — Floating Chatbot Button
   Placeholder UI for future backend integration.
   ═══════════════════════════════════════════════════════ */

export default function ChatbotButton() {
  const [open, setOpen] = useState(false);
  const [hasShownTooltip, setHasShownTooltip] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);

  /* Show a subtle "Need help?" tooltip after 8 seconds on first visit */
  useEffect(() => {
    if (hasShownTooltip) return;
    const timer = setTimeout(() => {
      setShowTooltip(true);
      setHasShownTooltip(true);
      setTimeout(() => setShowTooltip(false), 4000);
    }, 8000);
    return () => clearTimeout(timer);
  }, [hasShownTooltip]);

  return (
    <>
      {/* Tooltip nudge */}
      <AnimatePresence>
        {showTooltip && !open && (
          <motion.div
            initial={{ opacity: 0, x: 10, scale: 0.9 }}
            animate={{ opacity: 1, x: 0, scale: 1 }}
            exit={{ opacity: 0, x: 10, scale: 0.9 }}
            className="fixed bottom-[5.5rem] right-20 md:bottom-10 md:right-28 z-[60] px-3 py-2 bg-white rounded-xl shadow-apple-lg text-xs font-medium text-slate-700 border border-violet-100/50"
          >
            Need help? 👋
            <div className="absolute top-1/2 -right-1.5 -translate-y-1/2 w-3 h-3 bg-white border-r border-b border-violet-100/50 rotate-[-45deg]" />
          </motion.div>
        )}
      </AnimatePresence>

      {/* Floating button */}
      <motion.button
        onClick={() => { setOpen(!open); setShowTooltip(false); }}
        className="fixed bottom-20 right-5 md:bottom-8 md:right-8 z-[60] w-14 h-14 sm:w-16 sm:h-16 rounded-full bg-gradient-to-br from-violet-600 to-purple-600 text-white shadow-apple-xl flex items-center justify-center hover:scale-105 active:scale-95 transition-transform duration-200"
        whileTap={{ scale: 0.9 }}
        initial={{ scale: 0, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ delay: 2, type: "spring", stiffness: 200, damping: 15 }}
        aria-label={open ? "Close chat" : "Open chat"}
        style={{
          boxShadow:
            "0 8px 32px rgba(124, 58, 237, 0.35), 0 2px 8px rgba(0,0,0,0.1)",
        }}
      >
        <AnimatePresence mode="wait">
          {open ? (
            <motion.div
              key="close"
              initial={{ rotate: -90, opacity: 0 }}
              animate={{ rotate: 0, opacity: 1 }}
              exit={{ rotate: 90, opacity: 0 }}
              transition={{ duration: 0.15 }}
            >
              <X className="w-6 h-6" />
            </motion.div>
          ) : (
            <motion.div
              key="chat"
              initial={{ rotate: 90, opacity: 0 }}
              animate={{ rotate: 0, opacity: 1 }}
              exit={{ rotate: -90, opacity: 0 }}
              transition={{ duration: 0.15 }}
              className="relative"
            >
              <MessageCircle className="w-6 h-6" />
              {/* Notification dot */}
              <span className="absolute -top-1 -right-1 w-3 h-3 bg-green-400 rounded-full border-2 border-white animate-pulse" />
            </motion.div>
          )}
        </AnimatePresence>
      </motion.button>

      {/* Pulse ring behind button */}
      {!open && (
        <motion.div
          className="fixed bottom-20 right-5 md:bottom-8 md:right-8 z-[59] w-14 h-14 sm:w-16 sm:h-16 rounded-full pointer-events-none"
          animate={{
            boxShadow: [
              "0 0 0 0px rgba(139, 92, 246, 0.3)",
              "0 0 0 12px rgba(139, 92, 246, 0)",
            ],
          }}
          transition={{ duration: 2, repeat: Infinity, ease: "easeOut" }}
        />
      )}

      {/* Chat window */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.9 }}
            transition={{ type: "spring", stiffness: 300, damping: 25 }}
            className="fixed bottom-24 right-4 sm:bottom-28 sm:right-8 z-[61] w-[calc(100vw-2rem)] sm:w-[380px] max-h-[70vh] rounded-3xl overflow-hidden shadow-apple-xl"
            style={{
              background: "rgba(255,255,255,0.97)",
              backdropFilter: "blur(40px)",
              border: "1px solid rgba(255,255,255,0.8)",
            }}
          >
            {/* Header */}
            <div className="bg-gradient-to-r from-violet-600 to-purple-600 px-5 py-4">
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center backdrop-blur-sm shrink-0">
                    <img
                      src="/assets/images/Busigrow_Registered-1751003166599.png"
                      alt="BusiGrow"
                      className="w-6 h-6 object-contain"
                    />
                  </div>
                  <div>
                    <p className="text-white font-bold text-sm">BusiGrow AI</p>
                    <p className="text-white/70 text-xs flex items-center gap-1.5">
                      <span className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse" />
                      Online now
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setOpen(false)}
                  aria-label="Close chat"
                  className="w-9 h-9 shrink-0 rounded-xl bg-white/15 hover:bg-white/25 active:scale-95 text-white flex items-center justify-center transition-all"
                >
                  <X className="w-4.5 h-4.5" />
                </button>
              </div>
            </div>

            {/* Messages area */}
            <div className="p-5 h-64 overflow-y-auto">
              {/* Bot greeting */}
              <div className="flex gap-2.5 mb-4">
                <div className="w-7 h-7 rounded-full bg-violet-100 flex items-center justify-center shrink-0 mt-0.5">
                  <MessageCircle className="w-3.5 h-3.5 text-violet-600" />
                </div>
                <div className="bg-violet-50 rounded-2xl rounded-tl-md px-4 py-3 max-w-[80%]">
                  <p className="text-sm text-slate-700 leading-relaxed">
                    Hey there! 👋 I&apos;m the BusiGrow AI assistant. How can I help
                    you grow your business today?
                  </p>
                  <p className="text-[10px] text-slate-400 mt-1.5">Just now</p>
                </div>
              </div>

              {/* Quick replies */}
              <div className="flex flex-wrap gap-2 ml-9">
                {[
                  "Tell me about services",
                  "Get a free quote",
                  "Book a consultation",
                ].map((q) => (
                  <button
                    key={q}
                    className="px-3 py-1.5 text-xs font-medium text-violet-600 bg-violet-50 border border-violet-200 rounded-full hover:bg-violet-100 active:scale-95 transition-all"
                  >
                    {q}
                  </button>
                ))}
              </div>
            </div>

            {/* Input area */}
            <div className="px-4 py-3 border-t border-slate-100">
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  placeholder="Type a message..."
                  className="flex-1 px-4 py-2.5 text-sm bg-slate-50 rounded-xl border border-slate-200 focus:outline-none focus:border-violet-300 focus:ring-2 focus:ring-violet-100 transition-all placeholder:text-slate-400"
                  disabled
                />
                <button
                  className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-600 to-purple-600 text-white flex items-center justify-center shadow-sm hover:shadow-apple transition-shadow opacity-50 cursor-not-allowed"
                  disabled
                >
                  <Send className="w-4 h-4" />
                </button>
              </div>
              <p className="text-[10px] text-slate-400 text-center mt-2">
                Powered by BusiGrow AI — Backend coming soon
              </p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
